import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS } from 'common/constants/defaultTableParamsConstants'
import { useAppSelector } from 'common/hooks/redux'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import type { EncounterLabApiItem } from 'features/Procedure/state/procedure-api.type'
import { useFetchEncounterLabsQuery } from 'features/Procedure/state/procedure.api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { labsTableColumns } from './columns'
import styles from './labs.module.scss'

type LabsProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
}

export const Labs = ({ selectedPatientId, selectedEncounterUid }: LabsProps) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedLabId = useAppSelector((state) => state.procedureReducer.selectedLabId)
  const { handleSetSelectedLabId } = useProcedureActions()

  const [columns, setColumns] = useState<ObjectType[]>([])
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })

  const shouldSkipLabsQuery = !siteId || !selectedPatientId || !selectedEncounterUid

  const {
    data: labsData = [],
    isFetching: isFetchingLabs,
    isLoading: isLoadingLabs,
  } = useFetchEncounterLabsQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipLabsQuery,
    },
  )

  const handleSelectLab = useCallback(
    (lab: EncounterLabApiItem) => {
      handleSetSelectedLabId(lab.id)
    },
    [handleSetSelectedLabId],
  )

  // After the user switches encounter/patient nothing is auto-selected: the list
  // resets and the preview shows "No document selected" until a lab is picked.
  const initialContextRef = useRef({ selectedPatientId, selectedEncounterUid })
  const hasContextChanged =
    initialContextRef.current.selectedPatientId !== selectedPatientId ||
    initialContextRef.current.selectedEncounterUid !== selectedEncounterUid

  useEffect(() => {
    handleSetSelectedLabId(null)
  }, [selectedPatientId, selectedEncounterUid, handleSetSelectedLabId])

  // `labsData` still holds the previous encounter's labs while the new ones are fetching
  useEffect(() => {
    if (isFetchingLabs || hasContextChanged) return
    if (labsData.length > 0 && !selectedLabId) {
      const matchingLab = selectedEncounterUid
        ? labsData.find((lab) => lab.encounter_uuid === selectedEncounterUid)
        : null
      handleSetSelectedLabId(matchingLab ? matchingLab.id : labsData[0].id)
    } else if (labsData.length === 0) {
      handleSetSelectedLabId(null)
    }
  }, [
    labsData,
    selectedLabId,
    selectedEncounterUid,
    handleSetSelectedLabId,
    isFetchingLabs,
    hasContextChanged,
  ])

  useEffect(() => {
    if (isFetchingLabs) return
    if (selectedLabId && labsData.length > 0) {
      const stillExists = labsData.find((lab) => lab.id === selectedLabId)
      if (!stillExists) {
        handleSetSelectedLabId(labsData[0].id)
      }
    }
  }, [labsData, selectedLabId, handleSetSelectedLabId, isFetchingLabs])

  useEffect(() => {
    setColumns(
      labsTableColumns({
        selectedLabId,
        onSelectLab: handleSelectLab,
      }),
    )
  }, [selectedLabId, handleSelectLab])

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    setTableParams((prev) => ({ ...prev, total: labsData.length }))
  }, [labsData.length])

  const getRowClassName = useCallback(
    (record: EncounterLabApiItem) => {
      return selectedLabId === record.id ? styles.selectedRow : ''
    },
    [selectedLabId],
  )

  return (
    <div className={styles.container}>
      <Table
        itemKey='id'
        tableParams={tableParams}
        dataSource={labsData}
        setTableParams={setTableParams}
        columns={columns}
        isLoading={isFetchingLabs || isLoadingLabs}
        haveActions={false}
        pagination={false}
        rowClassName={getRowClassName}
      />
    </div>
  )
}
